import {
  CampaignEntryPauseReason,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignStepConversion,
  MarketingCampaignEntryLogEvent,
  MarketingCampaignEntryStatus,
  MarketingCampaignPauseReason,
  MarketingCampaignStatus,
  MarketingCampaignStepConversionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
  FieldValue,
  resolveWithConcurrency,
  toMoment,
} from '@principle-theorem/shared';
import { MarketingCampaignEntry } from './campaign-entry';
import { MarketingCampaignEntryLog } from './campaign-entry-log';

export class CampaignEntryPauseManager {
  /**
   * Pause entries that exceed the available budget.
   * Sorts entries by estimated remaining cost (cheapest first) to maximise
   * the number of entries that can continue within budget.
   *
   * Entries within budget are left untouched.
   * Entries exceeding budget are paused with PausedForPayment status.
   */
  static async pauseForPayment(
    entries: WithRef<IMarketingCampaignEntry>[],
    budget: number
  ): Promise<void> {
    const activeEntries = entries.filter(
      (entry) => entry.status === MarketingCampaignEntryStatus.Active
    );

    if (!activeEntries.length) {
      return;
    }

    const entriesWithCost =
      await CampaignEntryPauseManager._getEntriesWithRemainingCost(
        activeEntries
      );
    entriesWithCost.sort((entryA, entryB) => entryA.remainingCost - entryB.remainingCost);

    // Phase 1: Sequential budget allocation (pure arithmetic, no I/O)
    let remainingBudget = budget;
    const entriesToPause: WithRef<IMarketingCampaignEntry>[] = [];

    for (const { entry, remainingCost } of entriesWithCost) {
      if (remainingBudget >= remainingCost) {
        remainingBudget -= remainingCost;
      } else {
        entriesToPause.push(entry);
      }
    }

    if (!entriesToPause.length) {
      return;
    }

    // Phase 2: Concurrent Firestore writes
    await resolveWithConcurrency(entriesToPause, 5, async (entry) => {
      await MarketingCampaignEntry.pause(
        entry,
        CampaignEntryPauseReason.InsufficientFunds
      );

      await MarketingCampaignEntryLog.createEntryLog(
        entry,
        MarketingCampaignEntryLogEvent.StatusChange,
        {
          message: 'Paused — insufficient campaign budget',
        }
      );
    });

    await CampaignEntryPauseManager._autoPauseCampaign(
      activeEntries[0].campaignRef
    );
  }

  /**
   * Resume entries that were paused for payment.
   * Resumes entries in order of startedAt day (oldest first), then by
   * remaining cost (cheapest first within the same day). Greedily fills
   * budget, skipping entries that don't fit and backfilling with cheaper
   * entries from later days when possible.
   *
   * Note: autoBillingEnabled sort priority will be added in Phase 4.
   */
  static async resumePausedEntries(
    pausedEntries: WithRef<IMarketingCampaignEntry>[],
    availableBudget: number
  ): Promise<void> {
    const eligibleEntries = pausedEntries.filter(
      (entry) => entry.status === MarketingCampaignEntryStatus.PausedForPayment
    );

    if (!eligibleEntries.length) {
      return;
    }

    const entriesWithCost =
      await CampaignEntryPauseManager._getEntriesWithRemainingCost(
        eligibleEntries
      );

    entriesWithCost.sort((entryA, entryB) => {
      const dayA = entryA.entry.startedAt
        ? toMoment(entryA.entry.startedAt).startOf('day').valueOf()
        : 0;
      const dayB = entryB.entry.startedAt
        ? toMoment(entryB.entry.startedAt).startOf('day').valueOf()
        : 0;

      if (dayA !== dayB) {
        return dayA - dayB;
      }

      return entryA.remainingCost - entryB.remainingCost;
    });

    // Phase 1: Sequential budget allocation (pure arithmetic, no I/O)
    let remainingBudget = availableBudget;
    const entriesToResume: WithRef<IMarketingCampaignEntry>[] = [];

    for (const { entry, remainingCost } of entriesWithCost) {
      if (remainingBudget >= remainingCost) {
        remainingBudget -= remainingCost;
        entriesToResume.push(entry);
      }
    }

    // Phase 2: Concurrent Firestore writes
    await resolveWithConcurrency(entriesToResume, 5, async (entry) => {
      await MarketingCampaignEntry.resume(entry);

      await MarketingCampaignEntryLog.createEntryLog(
        entry,
        MarketingCampaignEntryLogEvent.StatusChange,
        {
          message: 'Resumed — budget available',
        }
      );
    });
  }

  /**
   * Restart a converted entry, setting it back to Pending.
   * Step re-queuing is handled separately.
   */
  static async restartEntry(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    await MarketingCampaignEntry.restart(entry);

    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange
    );
  }

  /**
   * Calculate the remaining cost for each entry by summing the
   * estimatedCostRemaining of its pending/queued step conversions.
   */
  private static async _getEntriesWithRemainingCost(
    entries: WithRef<IMarketingCampaignEntry>[]
  ): Promise<
    { entry: WithRef<IMarketingCampaignEntry>; remainingCost: number }[]
  > {
    return resolveWithConcurrency(entries, 10, async (entry) => {
        const stepConversions = await Firestore.getDocs(
          MarketingCampaignEntry.stepConversionCol(entry)
        );

        const remainingCost =
          CampaignEntryPauseManager._calculateRemainingCost(stepConversions);

        return { entry, remainingCost };
      });
  }

  /**
   * Sum the estimatedCostRemaining for step conversions that are
   * still pending or queued.
   */
  private static _calculateRemainingCost(
    stepConversions: WithRef<IMarketingCampaignStepConversion>[]
  ): number {
    const incompleteStatuses = [
      MarketingCampaignStepConversionStatus.Pending,
      MarketingCampaignStepConversionStatus.Queued,
    ];

    return stepConversions
      .filter((conversion) => !conversion.deleted && incompleteStatuses.includes(conversion.status))
      .reduce((sum, conversion) => sum + conversion.estimatedCostRemaining, 0);
  }

  /**
   * Auto-pause the parent campaign when entries are paused for insufficient funds.
   * This prevents the cron from creating new runs that would immediately get paused.
   */
  private static async _autoPauseCampaign(
    campaignRef: DocumentReference<IMarketingCampaign>
  ): Promise<void> {
    await Firestore.patchDoc(campaignRef, {
      status: MarketingCampaignStatus.Paused,
      pauseReason: MarketingCampaignPauseReason.InsufficientFunds,
      nextRunAt: FieldValue.delete(),
    });
  }
}
